import React, { useState } from 'react';
import classNames from 'classnames';
import { PaginationQueryParams } from '../../../../../../shared/types/request';
import { CallOutcome } from '../../types';
import { CallOutcomeContentTabs } from '../../enums';
import CallOutcomeTabPanes from './call-outcome-tabpanes';

type CallOutcomeContentProps = {
  customOutcomes: CallOutcome[];
  defaultOutcomes: CallOutcome[];
  customOutcomePagination: {
    options: Required<PaginationQueryParams> & {
      totalElements: number;
      search?: string;
    };
    pagesCount: number;
    count: number;
  };
  defaultOutcomePagination: {
    options: Required<PaginationQueryParams> & {
      totalElements: number;
      search?: string;
    };
    pagesCount: number;
    count: number;
  };
  isRequestPending: boolean;
  onCreateHandler: (event: React.MouseEvent<HTMLElement>) => void;
  onUpdateHandler: (outcome: CallOutcome) => void;
  onDeleteHandler: (outcome: CallOutcome) => void;
  sendGetCustomOutcomeRequest: (
    pagination: PaginationQueryParams & { search?: string },
  ) => void;
  sendGetDefaultOutcomeRequest: (
    pagination: PaginationQueryParams & { search?: string },
  ) => void;
  activeTabKey: string;
  setActiveTabKey: (val: string) => void;
};

const CallOutcomeContent: React.FC<CallOutcomeContentProps> = ({
  customOutcomes,
  defaultOutcomes,
  customOutcomePagination,
  defaultOutcomePagination,
  sendGetCustomOutcomeRequest,
  sendGetDefaultOutcomeRequest,
  onUpdateHandler,
  onDeleteHandler,
  onCreateHandler,
  isRequestPending,
  activeTabKey,
  setActiveTabKey,
}) => {
  const [search, setSearch] = useState('');

  const onTabChange = (tabKey: string) => {
    setActiveTabKey(tabKey);
  };

  const onCustomFieldPaginationOptionsChangeHandler = (
    newPartialOptions: Partial<PaginationQueryParams> & { search?: string },
  ) => {
    const { options } = customOutcomePagination;
    const payload = { ...options, ...newPartialOptions };
    delete payload.totalElements;
    sendGetCustomOutcomeRequest(payload);
  };

  const onDefaultFieldPaginationOptionsChangeHandler = (
    newPartialOptions: Partial<PaginationQueryParams> & { search?: string },
  ) => {
    const { options } = defaultOutcomePagination;
    const payload = { ...options, ...newPartialOptions };
    delete payload.totalElements;
    sendGetDefaultOutcomeRequest(payload);
  };

  const onSearch = () => {
    if (activeTabKey === CallOutcomeContentTabs.CustomOutcomes) {
      onCustomFieldPaginationOptionsChangeHandler({
        search,
        page: 1,
        limit: 25,
      });
    } else if (activeTabKey === CallOutcomeContentTabs.SystemOutcomes) {
      onDefaultFieldPaginationOptionsChangeHandler({
        search,
        page: 1,
        limit: 25,
      });
    }
  };

  const onClearSearch = () => {
    setSearch('');
    if (activeTabKey === CallOutcomeContentTabs.CustomOutcomes) {
      onCustomFieldPaginationOptionsChangeHandler({
        search: '',
        page: 1,
        limit: 25,
      });
    } else if (activeTabKey === CallOutcomeContentTabs.SystemOutcomes) {
      onDefaultFieldPaginationOptionsChangeHandler({
        search: '',
        page: 1,
        limit: 25,
      });
    }
  };

  const onAction = (key: string, row: CallOutcome) =>
    key === 'edit' ? onUpdateHandler(row) : onDeleteHandler(row);

  const containerClassName = classNames([
    'call-outcome-content-container',
    {
      'call-outcome-custom-tab':
        activeTabKey === CallOutcomeContentTabs.CustomOutcomes,
      'call-outcome-system-tab':
        activeTabKey === CallOutcomeContentTabs.SystemOutcomes,
    },
  ]);

  return (
    <div className={containerClassName}>
      <CallOutcomeTabPanes
        onCreateHandler={onCreateHandler}
        customOutcomes={customOutcomes}
        defaultOutcomes={defaultOutcomes}
        defaultOutcomePagination={defaultOutcomePagination}
        customOutcomePagination={customOutcomePagination}
        onActionHandler={onAction}
        onCustomOutcomePaginationOptionsChangeHandler={
          onCustomFieldPaginationOptionsChangeHandler
        }
        onDefaultOutcomePaginationOptionsChangeHandler={
          onDefaultFieldPaginationOptionsChangeHandler
        }
        activeTabKey={activeTabKey}
        onTabChange={onTabChange}
        search={search}
        setSearch={setSearch}
        onClearSearch={onClearSearch}
        onSearch={onSearch}
        isRequestPending={isRequestPending}
      />
    </div>
  );
};

export default CallOutcomeContent;
